import { logEvent } from 'firebase/analytics';
import { analyticsReady } from '../auth/firebaseAuth';
import { debugLog } from '../debug/devLogger';

// GA4 event name for a single GraphQL/Cloud-Function invocation. A single event
// carries the operation as a parameter, so every query/mutation is measurable
// and segmentable in GA4 (by operation, type, and status) without minting a
// distinct event name per operation.
const API_OPERATION_EVENT = 'api_operation';

// Extract the root GraphQL field — the operation/resolver name (e.g. `saveWAC`,
// `wacsByOrg`, `loggedInUser`) — from a GraphQL operation string. The UI passes
// the bare selection to GraphQLCore, so the first identifier is the operation.
// A leading `query`/`mutation` keyword (if present) is skipped. Returns
// 'unknown' for anything unparseable.
export const graphQLOperationName = (graphQL) => {
  if (typeof graphQL !== 'string') {
    return 'unknown';
  }
  const identifiers = graphQL.match(/[A-Za-z_]\w*/g) || [];
  const first = identifiers[0];
  if (!first) {
    return 'unknown';
  }
  if ((first === 'query' || first === 'mutation') && identifiers[1]) {
    return identifiers[1];
  }
  return first;
};

// Log one GA4 event, resolving the (possibly null) Analytics instance first.
// Analytics is best-effort: any failure (unsupported env, not initialized, no
// measurementId) is swallowed so it can never disturb the app.
export const logAnalyticsEvent = (name, params) =>
  analyticsReady
    .then((analytics) => {
      if (analytics) {
        logEvent(analytics, name, params);
      }
    })
    .catch(() => {});

// Measure the use of a single GraphQL operation (Cloud Function/resolver).
// `type` is 'query' or 'mutation'; `status` is 'success' or 'error'.
export const trackApiOperation = ({ operation, type, status, durationMs }) => {
  debugLog('analytics', API_OPERATION_EVENT, {
    operation,
    type,
    status,
    durationMs,
  });
  return logAnalyticsEvent(API_OPERATION_EVENT, {
    operation,
    operation_type: type,
    status,
    duration_ms: typeof durationMs === 'number' ? Math.round(durationMs) : undefined,
  });
};
